import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import VersionedComponent from "@components/VersionedComponent";
import { VERSION_CM_13766_SHARES_V3 } from "@cores/version";
import { Button, CircularProgress, Grid } from "@mzc-pdc/ui";
import EmptyList from "@components_v2/table-list/empty-list";
import TableList from "@components_v2/table-list/table-list";
import { EllipsisTypography } from "@components_v2/ellipsis-tooltip";
import { togglePropertyPanel } from "@modules/propertyPanel";
import { IconDownload } from "@mzc-cloudplex/icons";
import { TABLE_LIST_TYPE } from "@constants";
import ListTools from "@features/list-tools/list-tools";
import useDialog from "@hooks/useDialog";
import useTokenPagination from "@hooks/useTokenPagination";
import { Pagination } from "@components_v2/pgaination/pagination";
import TooltipButton from "@components_v2/tooltip-button/tooltip-button";
import { MySharingAssetsColumn } from "./columns-list-shared-v2";
import { withSearchQueryParamsContext } from "@components/params";
import * as yup from "yup";
import useDownloadJobs from "@hooks/useDownloadJobs";
import { listSharingAssetProjectsAPI } from "@modules/apis/shared-box-v2";
import { getMySharingAssets } from "@modules/shared-box-v2";
import DialogManageShareV2 from "@features/dialog/share/dialog-manage-share-v2";
import { siteMode } from "@cores/siteMode";

const ListSharedAssetsByMe = ({ searchQueryParams, updateFilterValueOptions }) => {
    const { spaceId, projectId } = useParams();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { dialog, openDialog, closeDialog } = useDialog();
    const { requestDownloadJob } = useDownloadJobs();
    const isLibrary = siteMode.get() === "CMS";

    const [selectedItems, setSelectedItems] = useState([]);
    const [clickedItem, setClickedItem] = useState(null);
    const [actionTarget, setActionTarget] = useState(null);

    const stage = useSelector(({ stage }) => stage);
    const sharedBoxV2 = useSelector(({ sharedBoxV2 }) => sharedBoxV2);

    const onHandleChangeLimit = useCallback(
        (limit) => {
            searchQueryParams.updateSearchQueryParams({
                action: "push",
                params: { ...searchQueryParams.params, limit },
            });
        },
        [searchQueryParams],
    );

    const limit = searchQueryParams?.params?.limit || 20;
    const projectIds = searchQueryParams?.params?.projectIds?.map((project) => project.id)?.join(",");

    const fetchPage = useCallback(
        async ({ nextToken, signal }) => {
            const response = await dispatch(getMySharingAssets({ nextToken, limit, projectIds }, signal));
            return response?.nextToken;
        },
        [dispatch, limit, projectIds],
    );

    const { paginationProps } = useTokenPagination({ fetchPage });

    const onHandleClickDownload = useCallback(
        (target) => {
            const targets = target ? [target] : selectedItems;

            // project.id별로 그룹화
            const groupedByProjectId = {};

            targets.forEach((item) => {
                const itemProjectId = item.project?.id || projectId;
                if (!groupedByProjectId[itemProjectId]) {
                    groupedByProjectId[itemProjectId] = [];
                }
                groupedByProjectId[itemProjectId].push(item);
            });

            // 각 프로젝트 그룹별로 다운로드 요청
            Object.entries(groupedByProjectId).forEach(([projectId, items]) => {
                const downloadRequestTargets = {
                    resourceId: items.length === 1 ? items[0].id : undefined,
                    resourceIds: items.length > 1 ? items.map((item) => item.id) : undefined,
                    projectId: projectId,
                    resourceName: items[0].name,
                };
                requestDownloadJob("ASSET", downloadRequestTargets, items.length, false);
            });

            setSelectedItems([]);
        },
        [selectedItems, projectId, requestDownloadJob],
    );

    const getListToolsButtons = useCallback(
        (isResponsive) => {
            return [
                <TooltipButton
                    key="download"
                    circled
                    title={t("common::label::Download", "Download")}
                    size={"small"}
                    onClickButton={() => {
                        onHandleClickDownload();
                    }}
                    buttonIcon={<IconDownload size={16} />}
                    buttonText={t("common::label::Download", "Download")}
                    isDivided={true}
                    isResponsive={isResponsive}
                />,
            ];
        },
        [selectedItems, requestDownloadJob],
    );

    const onHandleClickShare = (item) => {
        setActionTarget(item);
        openDialog("share");
    };

    const onHandleClickCloseShare = () => {
        closeDialog();
        setActionTarget(null);
    };

    const pagination = useMemo(() => {
        return (
            <Pagination
                {...paginationProps}
                limit={searchQueryParams.params.limit}
                onChangeLimit={onHandleChangeLimit}
            />
        );
    }, [paginationProps, searchQueryParams?.params?.limit, onHandleChangeLimit]);

    const parsedData = useMemo(() => {
        return sharedBoxV2?.data?.map(({ asset, id, ...rest }) => ({
            ...(asset ?? {}),
            id,
            assetId: asset?.id,
            expiresAt: rest?.option?.expiresAt,
            ...rest,
        }));
    }, [sharedBoxV2?.data]);

    const getFilterOptions = useCallback(async (filterIds) => {
        const results = {};

        // 각 필터 ID에 대해 병렬로 처리
        await Promise.all(
            filterIds.map(async (filterId) => {
                switch (filterId) {
                    case "projectIds": {
                        const projects = [];
                        let nextToken = null;
                        do {
                            const response = await listSharingAssetProjectsAPI(stage.endpoint, {
                                nextToken,
                                limit: 20,
                            });

                            projects.push(...response?.data?.results);
                            nextToken = response?.data?.nextToken;
                        } while (nextToken);

                        results[filterId] = projects.map(({ id, name }) => ({
                            id: id,
                            label: name,
                            value: id,
                            name: name,
                        }));
                        break;
                    }
                }
            }),
        );

        updateFilterValueOptions(results);
    }, []);

    useEffect(() => {
        searchQueryParams.updateSearchQueryParams({
            action: "replace",
            params: {
                ...searchQueryParams.params,
                limit: searchQueryParams?.params?.limit ?? 20,
                type: "SHARED_BY_ME",
            },
        });

        getFilterOptions(["projectIds"]);
    }, []);

    return (
        <VersionedComponent version={VERSION_CM_13766_SHARES_V3}>
            {dialog === "share" && (
                <DialogManageShareV2
                    selectedItem={{
                        id: actionTarget?.assetId,
                        name: actionTarget?.name,
                        summary: actionTarget,
                    }}
                    domain={"ASSET"}
                    onClickClose={onHandleClickCloseShare}
                />
            )}
            <ListTools
                domain={TABLE_LIST_TYPE.MY_SHARED_ASSETS}
                isCardView={false}
                getButtons={getListToolsButtons}
                pageCount={sharedBoxV2?.data?.length}
                numSelected={selectedItems.length}
                // onChangeNumSelected={onHandleChangeNumSelected}
                pagination={pagination}
                hiddenDivider={true}
            />
            {sharedBoxV2?.pending ? (
                <Grid item xs>
                    <Grid container alignItems={"center"} justifyContent={"center"} height={`100%`}>
                        <CircularProgress color="grey" />
                    </Grid>
                </Grid>
            ) : (
                <>
                    {parsedData?.length > 0 ? (
                        <TableList
                            keyPrefix={"my-shared-assets"}
                            type={"share_asset"}
                            columns={MySharingAssetsColumn}
                            data={parsedData}
                            selected={selectedItems}
                            clicked={clickedItem}
                            showColumns={true}
                            // onClickCheck={onHandleClickCheck}
                            onClickRow={(item) => {
                                setClickedItem(item);
                                if (isLibrary || !item.assetId) return;
                                dispatch(
                                    togglePropertyPanel({
                                        targetId: item.assetId,
                                        projectId: item.project.id,
                                        type: TABLE_LIST_TYPE.ASSET,
                                    }),
                                );
                            }}
                            actionButton={(item) => (
                                <Button
                                    size={"small"}
                                    color="secondary"
                                    variant="outlined"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onHandleClickShare(item);
                                    }}
                                    disabled={!item.assetId}
                                    sx={{
                                        maxWidth: "100%",
                                        // @mzc-pdc/ui Button은 children을 <span>으로 감싸며, 그 span이 flex item이라
                                        // min-width:auto 기본값 탓에 축소되지 않아 긴 라벨이 버튼 밖으로 넘친다.
                                        // 래퍼 span을 축소·클립 가능하게 만들어 안쪽 라벨이 말줄임되도록 한다. (DAM-306)
                                        "& > span:not(.MuiCircularProgress-root)": { minWidth: 0, overflow: "hidden" },
                                    }}
                                >
                                    {/* pt-BR 등 긴 라벨은 줄바꿈 대신 말줄임 처리하고, 잘렸을 때만 툴팁 노출 (DAM-306) */}
                                    {/* variant="inherit" Typography는 <span>(인라인)으로 렌더되므로 display:block로 말줄임 활성화 */}
                                    <EllipsisTypography
                                        noWrap
                                        variant={"inherit"}
                                        tooltipTitle={t("common::label::Shared management", "Shared management")}
                                        sx={{ display: "block", minWidth: 0 }}
                                    >
                                        {t("common::label::Shared management", "Shared management")}
                                    </EllipsisTypography>
                                </Button>
                            )}
                            actionButtonProps={{ width: 200 }}
                        />
                    ) : (
                        <EmptyList />
                    )}
                </>
            )}
        </VersionedComponent>
    );
};

export default withSearchQueryParamsContext({
    addedSchema: {
        projectIds: yup.array(),
        type: yup.string(),
    },
    setProps: true,
})(ListSharedAssetsByMe);
